import {useState} from "react";
import {Toggle, Message, toaster, Modal, Button, Whisper} from "rsuite";
import {ClockStation} from "../../../../Targets";
import {WriteTimeInChannelCommand, WriteChannelMode, ReadImpulse} from "../../../../Commands";
import {BitArrayToNumber, NumberToBitArray} from "../../../../Converters";
import UpdateButton from "../../../shared/UpdateButton/UpdateButton";
import GearIcon from "@rsuite/icons/Gear";
import {getTooltip} from "../../../../shared/Tooltip";
import {SecondaryClockPendingToggleChange, SecondaryClocksSectionProps} from "../MainPage.model";
import {SecondaryClockApi} from "../../../../services/SecondaryClock.api";

const SecondaryClocksSection = ({
                                  availableChannels,
                                  channelDescriptions,
                                  clockStates,
                                  voltage,
                                  channelData,
                                  loading,
                                  initialized,
                                  onTimeChange,
                                  onToggleChange,
                                  deviceId,
                                  executeCommand,
                                  onTabChange,
                                  onTimeWrite,
                                  onRefreshData,
                                  blockedChannels,
                                  relayStatus
                                }: SecondaryClocksSectionProps) => {
  const [toggleLoadingStates, setToggleLoadingStates] = useState<Record<number, boolean>>({});
  const [timeWriteLoading, setTimeWriteLoading] = useState<Record<number, boolean>>({});
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingToggleChange, setPendingToggleChange] = useState<SecondaryClockPendingToggleChange>({
    channelIndex: null,
    newState: null,
    clockId: null
  });

  const clockFaceSrc = "clock-face.svg?v=1.0";
  const hourHandSrc = "hour-hand.svg?v=1.0";
  const minuteHandSrc = "minute-hand.svg?v=1.0";

  const calculateHandAngles = (hours: number, minutes: number) => {
    const hourAngle = (hours % 12) * 30 + minutes * 0.5;
    const minuteAngle = minutes * 6;
    return {hourAngle, minuteAngle};
  };

  const handleSettingsClick = () => {
    if (onTabChange) {
      onTabChange('secondary');
    }
  };

  const checkRelayMode = (isChannelEnabled: boolean) => {
    return !!(isChannelEnabled && relayStatus?.relays[1]?.enabled);
  }

  const updateParameters = async () => {
    try {
      const parametersResponse = await SecondaryClockApi.getParams(String(deviceId));

      const resultImpulse = await executeCommand(ClockStation, ReadImpulse, []);
      const arr = NumberToBitArray(resultImpulse.Params[5]);

      if (onRefreshData) {
        await onRefreshData(parametersResponse, arr);
      }

    } catch (error) {
      console.error('Ошибка при обновлении параметров:', error);
    }
  };

  const handleRefreshSection = async () => {
    setRefreshLoading(true);
    try {
      await updateParameters();

      toaster.push(
        <Message type="success" closable>
          Данные вторичных часов успешно обновлены
        </Message>,
        {placement: 'topEnd', duration: 2000}
      );

    } catch (error) {
      console.error('Ошибка при обновлении данных:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка при обновлении данных вторичных часов
        </Message>,
        {placement: 'topEnd', duration: 3000}
      );
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleChannelStateChange = async (channelIndex: number, newState: boolean) => {
    const clockId = channelIndex + 1;

    if (!newState) {
      setPendingToggleChange({
        channelIndex,
        newState,
        clockId
      });
      setShowConfirmationModal(true);
      return;
    }

    await performChannelStateChange(channelIndex, newState, clockId);
  };

  const handleConfirmToggle = async () => {
    const {channelIndex, newState, clockId} = pendingToggleChange;
    setShowConfirmationModal(false);
    if (channelIndex === null || newState === null || clockId === null) return;
    await performChannelStateChange(channelIndex, newState, clockId);
    setPendingToggleChange({
      channelIndex: null,
      newState: null,
      clockId: null
    });
  };

  const handleCancelToggle = () => {
    setShowConfirmationModal(false);
    setPendingToggleChange({
      channelIndex: null,
      newState: null,
      clockId: null
    });
  };

  const performChannelStateChange = async (channelIndex: number, newState: boolean, clockId: number) => {
    setToggleLoadingStates(prev => ({
      ...prev,
      [clockId]: true
    }));

    try {
      onToggleChange(clockId, newState);

      const updatedChannelData = [...channelData];

      updatedChannelData[channelIndex] = {
        ...updatedChannelData[channelIndex],
        enabled: newState
      };

      const enabledList = updatedChannelData.map(x => x.enabled || false);
      const syncList = updatedChannelData.map(x => x.sync || false);
      const unionData = [...enabledList, ...syncList].reverse();

      const flag = BitArrayToNumber(unionData);

      const currentMode = 0;

      await executeCommand(ClockStation, WriteChannelMode, [currentMode, flag]);

      await new Promise(resolve => setTimeout(resolve, 500));
      await updateParameters();

      toaster.push(
        <Message type="success" closable>
          Состояние канала {clockId} успешно изменено
        </Message>,
        {placement: 'topEnd', duration: 2000}
      );

    } catch (error) {
      console.error(`Ошибка при изменении состояния канала ${channelIndex}:`, error);
      onToggleChange(clockId, !newState);

      toaster.push(
        <Message type="error" closable>
          Ошибка при изменении состояния канала {clockId}
        </Message>,
        {placement: 'topEnd', duration: 3000}
      );
    } finally {
      setToggleLoadingStates(prev => ({
        ...prev,
        [clockId]: false
      }));
    }
  };

  const handleTimeWrite = async (channelIndex: number) => {
    const channel = channelData[channelIndex];

    if (!channel.timeChanged) return;

    setTimeWriteLoading(prev => ({
      ...prev,
      [channelIndex]: true
    }));

    try {
      await executeCommand(
        ClockStation,
        WriteTimeInChannelCommand,
        [channelIndex + 1, channel.hours, channel.minutes]
      );

      await new Promise(resolve => setTimeout(resolve, 1000));

      await updateParameters();

      if (onTimeWrite) {
        onTimeWrite(channelIndex);
      }

      toaster.push(
        <Message type="success" closable>
          Время на канале {channelIndex + 1} успешно записано
        </Message>,
        {placement: 'topEnd', duration: 2000}
      );

    } catch (error) {
      console.error(`Ошибка при записи времени в канал ${channelIndex}:`, error);

      toaster.push(
        <Message type="error" closable>
          Ошибка при записи времени в канал {channelIndex + 1}
        </Message>,
        {placement: 'topEnd', duration: 3000}
      );
    } finally {
      setTimeWriteLoading(prev => ({
        ...prev,
        [channelIndex]: false
      }));
    }
  };

  const isTimeWriteLoading = (channelIndex: number) => {
    return timeWriteLoading[channelIndex] || false;
  };

  function getVoltageStyleByState(state: number | string) {
    let backgroundStyle = "none"

    const numericState = Number(state);

    if (numericState > 0) {
      backgroundStyle = "#ed6f6b"
    }

    if (numericState < 0) {
      backgroundStyle = "#7096f8"
    }

    return {
      background: backgroundStyle
    };
  }

  const getChannelDisplayName = (channelIndex: number) => {
    return `${channelIndex + 1} Канал управления`;
  };

  const getChannelLocation = (channelIndex: number) => {
    const locations = ["", "", "", ""];
    return locations[channelIndex] || 'Не указано';
  };

  return (
    <div className="fieldset-wrapper mt-3">
      <div className="legend-title main-legend-title">Вторичные часы</div>
      <section className="secondary-clock">
        <div className="row p-1">
          <div className="col-md-12 text-end">
            <UpdateButton
              loading={refreshLoading}
              onRefresh={handleRefreshSection}
            />
          </div>
        </div>
        <div className="row" style={{width: '100%', padding: '20px', paddingTop: '0', margin: 0}}>
          {availableChannels
            .filter((channel: any) => {
              return !(blockedChannels?.[channel.channel]);
            })
            .map((channel: any, index: number) => {
              const channelName = `${channel?.channel + 1} Канал управления`
              const description = channelDescriptions[channel.channel];
              const location = typeof description === 'string'
                ? description
                : description?.installationLocation || getChannelLocation(index);
              const {hourAngle, minuteAngle} = calculateHandAngles(channel.hours, channel.minutes);
              const clockId = channel.channel + 1;
              const isToggleLoading = toggleLoadingStates[clockId] || false;
              const isChannelEnabled = clockStates[clockId] !== undefined ? clockStates[clockId] : false;
              const isWritingTime = isTimeWriteLoading(channel.channel);

              return (
                <div key={channel.channel} className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4">
                  <div className="fieldset-wrapper control h-100 bg-white">
                    {(!initialized || loading) && (
                      <div className="card-loader-overlay">
                        <div className="card-loader">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Загрузка...</span>
                          </div>
                          <div className="loader-text">Загрузка данных...</div>
                        </div>
                      </div>
                    )}
                    <div className="legend-title">{channelName}</div>
                    <div className="clock-wrapper">
                      <div className={`clock-container ${(!initialized || loading) ? 'loading' : ''}`}>
                        <div className="toggle-container">
                          <Toggle
                            color="blue"
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                            loading={isToggleLoading}
                            checked={isChannelEnabled}
                            onChange={(checked) => handleChannelStateChange(channel.channel, checked)}
                            disabled={!initialized || loading || isToggleLoading}
                          >
                          </Toggle>
                        </div>
                        <div className="clock mt-1" style={{position: 'relative'}}>
                          <div className={checkRelayMode(isChannelEnabled) ? 'clock-background' : undefined}/>
                          <img
                            style={{
                              opacity: isChannelEnabled ? 1 : 0.3,
                              zIndex: 1
                            }}
                            className="clock-face"
                            src={clockFaceSrc}
                            alt="clock face"
                          />

                          <img
                            className="clock-hand hour-hand"
                            src={hourHandSrc}
                            alt="hour hand"
                            style={{
                              transform: `rotate(${hourAngle}deg)`,
                              opacity: isChannelEnabled ? 1 : 0.3,
                              zIndex: 2
                            }}
                          />
                          <img
                            className="clock-hand minute-hand"
                            src={minuteHandSrc}
                            alt="minute hand"
                            style={{
                              transform: `rotate(${minuteAngle}deg)`,
                              opacity: isChannelEnabled ? 1 : 0.3,
                              zIndex: 3
                            }}
                          />
                        </div>
                      </div>

                      <div className="clock-location">
                        <span className="text-center">{location}</span>
                      </div>

                      <div className="w-100">
                        <div className="row my-1">
                          <div className="col-6" style={{padding: 3}}>
                            <div className="input-group mb-3">
                              <div className="settings-input-group-text input-group-text">
                                <label className="settings-form-label form-label">Напряж.</label>
                              </div>
                              <input
                                className="settings-form-control form-control clock-control"
                                readOnly={true}
                                value={voltage || ''}
                                style={getVoltageStyleByState(channel.state)}
                                disabled={!initialized}
                              />
                              <div className="settings-input-group-text input-group-text input-group-text-wrapper">
                                <div>
                                  В
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-6" style={{padding: 3}}>
                            <div className="input-group mb-3">
                              <div className="settings-input-group-text input-group-text">
                                <div className="settings-form-label form-label input-group-text-wrapper">
                                  <div>
                                    Ток
                                  </div>
                                </div>
                              </div>
                              <input
                                className="settings-form-control form-control clock-control"
                                readOnly={true}
                                value={channel.amperage || ''}
                                disabled={!initialized}
                              />
                              <div className="settings-input-group-text input-group-text input-group-text-wrapper">
                                <div>
                                  A
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="time-input-container w-100">
                        <label className="time-label" htmlFor={`time-input-${channel.channel}`} style={{textAlign: "center"}}>
                          Положение стрелок
                        </label>
                        <div className="time-wrapper">
                          <input
                            id={`time-input-${channel.channel}`}
                            type="time"
                            value={`${String(channel.hours).padStart(2, '0')}:${String(channel.minutes).padStart(2, '0')}`}
                            onChange={(e) => onTimeChange(e, channel.channel)}
                            className="settings-form-control relay-time-control time-input form-control"
                            disabled={!initialized || loading}
                          />
                          <button
                            className="time-btn"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleTimeWrite(channel.channel);
                            }}
                            disabled={!channel.timeChanged || loading || !initialized || isWritingTime}
                          >
                            {isWritingTime ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Запись...
                              </>
                            ) : (
                              "Записать"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        <div className="settings" style={{padding: '5px'}}>
          <a
            className="settings-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleSettingsClick();
            }}
          >
            <Whisper
              placement="topStart"
              controlId="control-id-hover"
              trigger="hover"
              speaker={getTooltip('Перейти в настройки')}>
            <GearIcon className="gear-icon"></GearIcon>
            </Whisper>
          </a>
        </div>
      </section>

      <Modal
        style={{zIndex: 999999}}
        open={showConfirmationModal}
        onClose={handleCancelToggle}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Подтверждение выключения</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите выключить {getChannelDisplayName(pendingToggleChange.channelIndex ?? 0)}?
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleConfirmToggle}
            appearance="primary"
            color="red"
          >
            Выключить
          </Button>
          <Button
            onClick={handleCancelToggle}
            appearance="subtle"
          >
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SecondaryClocksSection;
